export interface AuthFeature {
  title: string
  description: string
}

export interface AuthPageMarketing {
  panelKicker: string
  panelTitleLine1: string
  panelTitleEmphasis: string
  panelSubheadline: string
  features: AuthFeature[]
  formBadge: string
  formTitle: string
  formSubheadline: string
  submitLabel: string
  secondaryPrompt: string
  secondaryActionLabel: string
  footerNote: string
}

const signupDefaults: AuthPageMarketing = {
  panelKicker: 'Start with one outcome',
  panelTitleLine1: 'Build the operation around',
  panelTitleEmphasis: 'the work that matters.',
  panelSubheadline:
    'Give Ricald an outcome. It begins with the context already available and asks for access only when the work needs it.',
  features: [
    {
      title: 'Start with useful work',
      description:
        'Describe the result you need. CRM and Ads remain distinct connected sources, requested only when the work needs them.',
    },
    {
      title: 'Grant access in context',
      description: 'When work needs CRM, email, calendar, or a browser, Ricald asks for that exact access and then resumes.',
    },
    {
      title: 'Let outcomes improve the next cycle',
      description: 'Keep decisions, actions, replies, meetings, and results connected to the objective.',
    },
  ],
  formBadge: 'New workspace',
  formTitle: 'Create your account',
  formSubheadline: 'Begin with one objective. Add complexity only when it earns its place.',
  submitLabel: 'Create account',
  secondaryPrompt: 'Already have an account?',
  secondaryActionLabel: 'Sign in',
  footerNote: 'Your operating boundaries remain visible and changeable.',
}

const loginDefaults: AuthPageMarketing = {
  panelKicker: 'Return to the operation',
  panelTitleLine1: 'The work kept',
  panelTitleEmphasis: 'its context.',
  panelSubheadline:
    'Open Ricald to see what moved, what stayed inside authority, and which decision now requires your judgment.',
  features: [
    {
      title: 'Work in motion',
      description: 'Research, qualification, and approved actions remain attached to the commercial objective.',
    },
    {
      title: 'Decisions waiting',
      description: 'Exceptions arrive with the reason, evidence, confidence, and authority boundary attached.',
    },
    {
      title: 'Outcomes learning',
      description: 'What happened remains connected to the action that produced it and informs what happens next.',
    },
  ],
  formBadge: 'Workspace access',
  formTitle: 'Welcome back',
  formSubheadline: 'Continue to your commercial operation.',
  submitLabel: 'Sign in',
  secondaryPrompt: "Don't have an account?",
  secondaryActionLabel: 'Get started',
  footerNote: 'Your work, decisions, and activity history are waiting.',
}

function resolvePublicCta(candidate: string | undefined, fallback: string): string {
  if (!candidate) return fallback
  return /free/i.test(candidate) ? fallback : candidate
}

function getAuthFeatures(marketingCopy: Record<string, string>, prefix: string, fallback: AuthFeature[]): AuthFeature[] {
  return fallback.map((feature, index) => ({
    title: marketingCopy[`${prefix}_feature_${index + 1}_title`] || feature.title,
    description: marketingCopy[`${prefix}_feature_${index + 1}_description`] || feature.description,
  }))
}

function buildAuthMarketing(
  marketingCopy: Record<string, string>,
  prefix: string,
  fallback: AuthPageMarketing
): AuthPageMarketing {
  return {
    panelKicker: marketingCopy[`public_v3_${prefix}_panel_kicker`] || fallback.panelKicker,
    panelTitleLine1: marketingCopy[`public_v3_${prefix}_panel_title_line_1`] || fallback.panelTitleLine1,
    panelTitleEmphasis: marketingCopy[`public_v3_${prefix}_panel_title_emphasis`] || fallback.panelTitleEmphasis,
    panelSubheadline: marketingCopy[`public_v3_${prefix}_panel_subheadline`] || fallback.panelSubheadline,
    features: getAuthFeatures(marketingCopy, `public_v3_${prefix}`, fallback.features),
    formBadge: resolvePublicCta(marketingCopy[`public_v3_${prefix}_form_badge`], fallback.formBadge),
    formTitle: marketingCopy[`public_v3_${prefix}_form_title`] || fallback.formTitle,
    formSubheadline: marketingCopy[`public_v3_${prefix}_form_subheadline`] || fallback.formSubheadline,
    submitLabel: marketingCopy[`public_v3_${prefix}_submit_label`] || fallback.submitLabel,
    secondaryPrompt: marketingCopy[`public_v3_${prefix}_secondary_prompt`] || fallback.secondaryPrompt,
    secondaryActionLabel: resolvePublicCta(marketingCopy[`public_v3_${prefix}_secondary_action_label`], fallback.secondaryActionLabel),
    footerNote: resolvePublicCta(marketingCopy[`public_v3_${prefix}_footer_note`], fallback.footerNote),
  }
}

export function getSignupMarketing(marketingCopy: Record<string, string>): AuthPageMarketing {
  return buildAuthMarketing(marketingCopy, 'auth_signup', signupDefaults)
}

export function getLoginMarketing(marketingCopy: Record<string, string>): AuthPageMarketing {
  return buildAuthMarketing(marketingCopy, 'auth_login', loginDefaults)
}
